/**
 * PantryMatchStrip
 *
 * Shows which pantry ingredients a meal uses, displayed as colored pills.
 * Vedette (delivery) items get a star, expiring items get a clock,
 * and regular matches get a checkmark.
 */

import React, { useMemo } from 'react';
import { View, Text, StyleSheet, ScrollView } from 'react-native';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { selectActiveInventory } from '../../redux/inventorySlice';
import { getInventoryMatchesForRecipe, ingredientMatchText } from '../../services/inventoryMatchService';
import { isLabelForMatching } from '../../utils/inlineIngredientHeader';
import { PlannedMeal } from '../../redux/mealPlanSlice';
import {
  Paprika,
  PaprikaTypography,
  PaprikaSpacing,
  PaprikaRadius,
} from '../../styles/theme';

interface PantryMatchStripProps {
  meal: PlannedMeal;
}

interface IngredientPill {
  name: string;
  variant: 'vedette' | 'expiring' | 'match';
}

const MAX_VISIBLE_PILLS = 6;

const PantryMatchStrip: React.FC<PantryMatchStripProps> = ({ meal }) => {
  const { t } = useTranslation();
  const activeInventory = useSelector(selectActiveInventory);

  const { pills, matchedCount, totalCount } = useMemo(() => {
    const ingredients = meal.recipe?.extendedIngredients;
    if (!ingredients || ingredients.length === 0) {
      return { pills: [], matchedCount: 0, totalCount: 0 };
    }

    const matches = getInventoryMatchesForRecipe(ingredients, activeInventory);
    const vedetteIngredient = meal.matchMetadata?.vedetteIngredient?.toLowerCase();
    const expiringNames = (meal.matchMetadata?.usesExpiringIngredients || []).map(n => n.toLowerCase());

    const result: IngredientPill[] = [];
    let matched = 0;

    matches.forEach((matchResult, ingredientName) => {
      if (!matchResult.matched) return;
      matched++;

      const nameLower = ingredientName.toLowerCase();
      let variant: IngredientPill['variant'] = 'match';

      if (vedetteIngredient && nameLower.includes(vedetteIngredient)) {
        variant = 'vedette';
      } else if (expiringNames.some(e => nameLower.includes(e) || e.includes(nameLower))) {
        variant = 'expiring';
      }

      result.push({ name: ingredientName, variant });
    });

    // Sort: vedette first, then expiring, then regular
    const order = { vedette: 0, expiring: 1, match: 2 };
    result.sort((a, b) => order[a.variant] - order[b.variant]);

    // "For the lemon curd" is a label, not an ingredient: it is neither in the
    // pantry nor something to buy, so it is not one of the N either.
    const totalCount = ingredients.filter(ing => !isLabelForMatching(ingredientMatchText(ing))).length;
    return { pills: result, matchedCount: matched, totalCount };
  }, [meal.recipe?.extendedIngredients, activeInventory, meal.matchMetadata]);

  // Don't render for free-text meals or meals without ingredients
  if (!meal.recipe?.extendedIngredients || totalCount === 0) return null;

  const allMatched = matchedCount === totalCount;
  const noneMatched = matchedCount === 0;

  // Header text
  let headerText: string;
  if (allMatched) {
    headerText = t('pantryMatch.all', { count: totalCount });
  } else if (noneMatched) {
    headerText = t('pantryMatch.none', { count: totalCount });
  } else {
    headerText = t('pantryMatch.covers', { matched: matchedCount, total: totalCount });
  }

  const visiblePills = pills.slice(0, MAX_VISIBLE_PILLS);
  const overflowCount = pills.length - visiblePills.length;
  const missingCount = totalCount - matchedCount;

  return (
    <View style={styles.container}>
      <Text style={[styles.header, allMatched && styles.headerSuccess]}>
        {headerText}
      </Text>

      {pills.length > 0 && (
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={styles.pillsContainer}
        >
          {visiblePills.map((pill, index) => (
            <View
              key={index}
              style={[
                styles.pill,
                pill.variant === 'vedette' && styles.pillVedette,
                pill.variant === 'expiring' && styles.pillExpiring,
                pill.variant === 'match' && styles.pillMatch,
              ]}
            >
              <Text style={styles.pillIcon}>
                {pill.variant === 'vedette' ? '\u2605' : pill.variant === 'expiring' ? '\u23F1' : '\u2713'}
              </Text>
              <Text style={[styles.pillText, pill.variant === 'vedette' && styles.pillTextVedette]} numberOfLines={1}>
                {pill.name}
              </Text>
            </View>
          ))}
          {overflowCount > 0 && (
            <View style={[styles.pill, styles.pillMatch]}>
              <Text style={styles.pillText}>{t('pantryMatch.more', { count: overflowCount })}</Text>
            </View>
          )}
        </ScrollView>
      )}

      {!noneMatched && missingCount > 0 && (
        <Text style={styles.missingText}>
          {t('pantryMatch.needToBuy', { count: missingCount })}
        </Text>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    paddingVertical: PaprikaSpacing.sm,
    paddingHorizontal: PaprikaSpacing.xs,
  },
  header: {
    ...PaprikaTypography.caption,
    color: Paprika.textSecondary,
    marginBottom: PaprikaSpacing.xs,
  },
  headerSuccess: {
    color: Paprika.success,
    fontWeight: '600',
  },
  pillsContainer: {
    flexDirection: 'row',
    gap: PaprikaSpacing.xs,
  },
  pill: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: PaprikaSpacing.sm,
    paddingVertical: 3,
    borderRadius: PaprikaRadius.xl,
    gap: 3,
  },
  pillVedette: {
    backgroundColor: '#FFF8F0',
  },
  pillExpiring: {
    backgroundColor: '#FFF3E0',
  },
  pillMatch: {
    backgroundColor: Paprika.accentLight,
  },
  pillIcon: {
    fontSize: 10,
    color: Paprika.accent,
  },
  pillText: {
    ...PaprikaTypography.caption,
    fontSize: 11,
    color: Paprika.textSecondary,
  },
  pillTextVedette: {
    fontWeight: '600',
  },
  missingText: {
    ...PaprikaTypography.caption,
    color: Paprika.textMuted,
    marginTop: PaprikaSpacing.xs,
  },
});

export default PantryMatchStrip;
